import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, Plus, Eye } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function KnowledgebasePage() {
  const { isStaff } = useAuth();
  const navigate    = useNavigate();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles]     = useState([]);
  const [search, setSearch]         = useState('');
  const [filterCat, setFilterCat]   = useState('');
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/kb/categories'),
      api.get('/kb/articles'),
    ]).then(([cRes, aRes]) => {
      setCategories(cRes.categories || []);
      setArticles(aRes.articles || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = articles.filter(a => {
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || (a.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchCat    = !filterCat || a.category?.id === filterCat;
    return matchSearch && matchCat;
  });

  return (
    <div>
      <div className="page-header">
        <h2>Knowledgebase</h2>
        {isStaff && (
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/admin/kb?new=1')}>
            <Plus size={14} /> New Article
          </button>
        )}
      </div>

      <div className="page-body">
        <div className="flex gap-3 mb-6">
          <div className="search-bar flex-1">
            <Search size={15} />
            <input className="form-input" placeholder="Search articles…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-select" style={{ width: 180 }} value={filterCat}
            onChange={e => setFilterCat(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <BookOpen size={36} /><h3>No articles found</h3>
            <p>{search ? 'Try different search terms' : 'No articles published yet'}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {categories.map(cat => {
              const arts = filtered.filter(a => a.category?.id === cat.id);
              if (arts.length === 0) return null;
              return (
                <div key={cat.id} className="card" style={{ padding: 0 }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>{cat.icon}</span>
                    <div>
                      <div className="font-semibold" style={{ fontSize: 13.5 }}>{cat.name}</div>
                      <div className="text-muted text-sm">{arts.length} article{arts.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  {arts.map((a, i) => (
                    <div key={a.id} style={{
                      padding: '11px 18px',
                      borderBottom: i < arts.length - 1 ? '1px solid var(--border)' : 'none',
                      cursor: 'pointer', transition: 'background 0.1s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                      onClick={() => navigate(`/knowledgebase/${a.slug}`)}>
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <div className="font-medium" style={{ fontSize: 13.5, marginBottom: 2 }}>{a.title}</div>
                          {(a.tags || []).length > 0 && (
                            <div className="flex gap-1" style={{ flexWrap: 'wrap' }}>
                              {a.tags.slice(0, 3).map(tag => (
                                <span key={tag} style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', fontSize: 11, padding: '1px 6px', borderRadius: 999, border: '1px solid var(--border)' }}>{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-muted text-sm" style={{ flexShrink: 0 }}>
                          <Eye size={11} />{a.views}
                        </div>
                      </div>
                      {isStaff && a.status !== 'published' && (
                        <span style={{ fontSize: 10.5, padding: '1px 6px', background: 'var(--amber-light)', color: 'var(--amber)', borderRadius: 999, marginTop: 4, display: 'inline-block', textTransform: 'capitalize' }}>
                          {a.status}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
